import React, {useEffect, useMemo, useState} from 'react';
import {useLocation} from "react-router-dom";
import {useSelector} from "react-redux";
import {PageHeader} from "../../../components/PageHeader";
import CodeManageSettingsLayout from "../fieldMng/shared/CodeManageSettingsLayout";
import CodeManageItemList from "../fieldMng/shared/CodeManageItemList";
import GroupCodeEditor from "./GroupCodeEditor";
import {
    GROUP_CODE_MANAGE_CATEGORIES,
    GROUP_CODE_MANAGE_ROUTE_CATEGORY,
    PURCHASE_ROUTE_SUB_NAV,
    PURCHASE_SUB_NAV,
    resolveGroupCodeCategoryMeta,
} from "./groupCodeCategoryConfig";
import {
    filterCategoriesByPlatform,
    pickActiveCategoryId,
} from "./menuPlatformFilter";
import {useGroupCodeCategory} from "./useGroupCodeCategory";
import "../fieldMng/shared/unifiedCodeManage.css";
import "../fieldMng/shared/codeManageSettings.css";
import "./groupCodeManage.css";

const SERVICE_SUB_NAV = [
    {id: "code", label: "코드관리"},
    {id: "handler", label: "처리담당자"},
];

function GroupCodeManage() {
    const location = useLocation();
    const platFrom = useSelector((state) => state.auth.platFrom);

    const visibleCategories = useMemo(
        () => filterCategoriesByPlatform(GROUP_CODE_MANAGE_CATEGORIES, platFrom),
        [platFrom],
    );

    const [activeCategory, setActiveCategory] = useState(() =>
        pickActiveCategoryId(
            GROUP_CODE_MANAGE_CATEGORIES,
            GROUP_CODE_MANAGE_ROUTE_CATEGORY[location.pathname],
            platFrom,
        ),
    );
    const [purchaseSubNav, setPurchaseSubNav] = useState(
        () => PURCHASE_ROUTE_SUB_NAV[location.pathname] ?? "method",
    );

    const activeMeta = useMemo(
        () => visibleCategories.find((item) => item.id === activeCategory)
            ?? visibleCategories[0]
            ?? null,
        [activeCategory, visibleCategories],
    );

    const effectiveMeta = useMemo(
        () => resolveGroupCodeCategoryMeta(activeMeta, purchaseSubNav),
        [activeMeta, purchaseSubNav],
    );

    const groupCode = useGroupCodeCategory(effectiveMeta);

    useEffect(() => {
        const preferred = GROUP_CODE_MANAGE_ROUTE_CATEGORY[location.pathname] ?? activeCategory;
        const nextId = pickActiveCategoryId(GROUP_CODE_MANAGE_CATEGORIES, preferred, platFrom);
        if (nextId && nextId !== activeCategory) {
            setActiveCategory(nextId);
        }
        if (PURCHASE_ROUTE_SUB_NAV[location.pathname]) {
            setPurchaseSubNav(PURCHASE_ROUTE_SUB_NAV[location.pathname]);
        }
    }, [location.pathname, platFrom, visibleCategories]);

    useEffect(() => {
        if (activeCategory !== "purchase") {
            setPurchaseSubNav("method");
        }
    }, [activeCategory]);

    const handleCategoryChange = (categoryId) => {
        setActiveCategory(categoryId);
    };

    if (!visibleCategories.length) {
        return (
            <div className="common-container container-height-full">
                <PageHeader
                    header="일반코드 관리"
                    indicator="환경설정 > 코드관리 > 일반코드 > 일반코드 관리"
                />
                <p className="group-code-manage__empty-platform">
                    현재 플랫폼({platFrom ?? "-"})에서 사용할 수 있는 일반코드 항목이 없습니다.
                </p>
            </div>
        );
    }

    if (!groupCode || !activeMeta || !effectiveMeta) {
        return null;
    }

    const panelSubNav = activeMeta.kind === "service"
        ? SERVICE_SUB_NAV
        : activeMeta.kind === "purchase"
            ? PURCHASE_SUB_NAV
            : null;
    const panelSubNavValue = activeMeta.kind === "service"
        ? groupCode.serviceSubNav
        : activeMeta.kind === "purchase"
            ? purchaseSubNav
            : null;
    const onPanelSubNavChange = activeMeta.kind === "service"
        ? groupCode.setServiceSubNav
        : activeMeta.kind === "purchase"
            ? setPurchaseSubNav
            : null;

    return (
        <div className="common-container container-height-full">
            <PageHeader
                header="일반코드 관리"
                indicator="환경설정 > 코드관리 > 일반코드 > 일반코드 관리"
            />

            <CodeManageSettingsLayout
                navItems={visibleCategories}
                activeCategory={activeCategory}
                onCategoryChange={handleCategoryChange}
                panelTitle={activeMeta.title}
                panelDescription={activeMeta.description}
                panelSubNav={panelSubNav}
                panelSubNavValue={panelSubNavValue}
                onPanelSubNavChange={onPanelSubNavChange}
                list={
                    <CodeManageItemList
                        addLabel={effectiveMeta.addLabel}
                        createSectionLabel="코드 추가"
                        items={groupCode.listData}
                        selectedItemId={groupCode.selectedCode?.id}
                        onSelect={groupCode.handleSelectCode}
                        onCreateRoot={groupCode.handleCreateRoot}
                        useAssetGroupTree={effectiveMeta.supportsChildCodes !== false}
                        emptyMessage="등록된 코드가 없습니다. 아래에서 추가하세요."
                    />
                }
                editor={
                    <GroupCodeEditor
                        categoryKind={effectiveMeta.kind}
                        assetCodeType={effectiveMeta.assetCodeType}
                        nameLabel={effectiveMeta.nameLabel}
                        supportsApprovalLines={!!effectiveMeta.supportsApprovalLines}
                        supportsApprovalBeforeOnly={!!effectiveMeta.supportsApprovalBeforeOnly}
                        supportsChildCodes={effectiveMeta.supportsChildCodes !== false}
                        code={groupCode.selectedCode}
                        setCode={groupCode.setSelectedCode}
                        onUpdate={groupCode.handleUpdate}
                        onDelete={groupCode.handleDelete}
                        onCreateChild={groupCode.handleCreateChild}
                        onUpdateChild={groupCode.handleUpdateChild}
                        onDeleteChild={groupCode.handleDelete}
                        serviceSubNav={groupCode.serviceSubNav}
                    />
                }
            />
        </div>
    );
}

export default GroupCodeManage;
